/**
 * Oath S45 — <oath-mega-menu> web component.
 *
 * GENUINE GAP / native-base+custom: hybrid click-toggle + hover-open desktop
 * mega-menu that INTERCEPTS the native SHOP nav trigger (reuses it) and shows a
 * custom panel. Justified per the Figma mega-menu layout.
 *
 * S48 HARDENING: refactored from raw `extends HTMLElement` (IIFE) to extend
 * Horizon's `@theme/component` Component (ES module). Own-DOM panel handle is now
 * a declarative ref (`ref="panel"` in the liquid) + super lifecycle. The
 * TRIGGER DISCOVERY (finding the external SHOP nav link via data-attr / header
 * text-match + retry) stays imperative — it targets an EXTERNAL element, not a
 * child ref. All open/close/hover-intent/timer/aria logic preserved byte-for-byte.
 * L-OATH-S48-MEGA-MENU-HARDEN.
 *
 * Interactions: click trigger → toggle; hover trigger (fine pointer) → open after
 * open-delay; leave trigger AND panel → close after close-delay; enter panel →
 * cancel close; Escape → close + refocus trigger; outside-click → close.
 * State: hidden attr = closed, .is-open = open; trigger aria-expanded mirrored.
 */

import { Component } from '@theme/component';

class OathMegaMenu extends Component {
  constructor() {
    super();
    this._isOpen = false;
    this._openTimer = null;
    this._closeTimer = null;
    this._onDocClick = this._onDocClick.bind(this);
    this._onDocKey = this._onDocKey.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onTriggerEnter = this._onTriggerEnter.bind(this);
    this._onTriggerLeave = this._onTriggerLeave.bind(this);
    this._onPanelEnter = this._onPanelEnter.bind(this);
    this._onPanelLeave = this._onPanelLeave.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    const handle = (this.dataset.triggerHandle || 'shop').toLowerCase();
    this._openDelay = parseInt(this.dataset.openDelay, 10) || 150;
    this._closeDelay = parseInt(this.dataset.closeDelay, 10) || 300;
    // Own-DOM panel via Component ref (ref="panel" in the liquid).
    this._panel = this.refs.panel;

    // Trigger discovery is deferred: the header section may render after us
    // (or we re-bind if the nav re-renders). Re-query each session-start +
    // a small retry loop covers async-loaded nav patterns.
    this._wireTrigger(handle);

    // Outside-click + Esc are global; safe to wire once.
    document.addEventListener('click', this._onDocClick, { capture: true });
    document.addEventListener('keydown', this._onDocKey);

    if (this._panel) {
      this._panel.addEventListener('mouseenter', this._onPanelEnter);
      this._panel.addEventListener('mouseleave', this._onPanelLeave);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._onDocClick, { capture: true });
    document.removeEventListener('keydown', this._onDocKey);
    this._clearTimers();
    if (this._trigger) {
      this._trigger.removeEventListener('click', this._onTriggerClick);
      this._trigger.removeEventListener('mouseenter', this._onTriggerEnter);
      this._trigger.removeEventListener('mouseleave', this._onTriggerLeave);
    }
    if (this._panel) {
      this._panel.removeEventListener('mouseenter', this._onPanelEnter);
      this._panel.removeEventListener('mouseleave', this._onPanelLeave);
    }
  }

  _wireTrigger(handle) {
    // Primary: explicit data-attribute (set via header.liquid @oath-override if present).
    const explicitSel = `[data-oath-mega-menu-trigger="${handle}"]`;
    // Fallback: text-match any nav link/button inside the header section whose visible
    // text (uppercased) equals the handle (uppercased). Auto-installs the data-attribute
    // for future runs. This avoids needing to vendor-edit Horizon's header.liquid.
    const wantText = handle.trim().toUpperCase();
    let attempts = 0;
    const tryBind = () => {
      let trigger = document.querySelector(explicitSel);
      if (!trigger) {
        // Scope to header region(s) only — don't tag links in footer / body content.
        // Prefer VISIBLE elements: at desktop viewport, Horizon renders BOTH the
        // top-nav SHOP link AND a duplicate inside the mobile menu-drawer (which
        // is display:none at >=750px). DOM order has the drawer first, so a naive
        // first-match binds an invisible trigger. Fix: filter to visible-only +
        // skip anything inside `.menu-drawer*` containers.
        const headerEls = document.querySelectorAll('header, .section-header, [class*="section--header"], [class*="header-group"], #header-group');
        for (const root of headerEls) {
          const candidates = root.querySelectorAll('a, button, summary');
          for (const el of candidates) {
            // Skip mobile drawer (and any other hidden) clones.
            if (el.closest('.menu-drawer, [class*="menu-drawer__"], [aria-hidden="true"]')) continue;
            if (el.offsetParent === null) continue;  // currently display:none / disconnected
            const txt = (el.textContent || '').trim().toUpperCase();
            if (txt === wantText) {
              el.setAttribute('data-oath-mega-menu-trigger', handle);
              trigger = el;
              break;
            }
          }
          if (trigger) break;
        }
      }
      if (trigger) {
        this._trigger = trigger;
        trigger.setAttribute('aria-haspopup', 'menu');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', this.id);
        trigger.addEventListener('click', this._onTriggerClick);
        trigger.addEventListener('mouseenter', this._onTriggerEnter);
        trigger.addEventListener('mouseleave', this._onTriggerLeave);
        return;
      }
      attempts += 1;
      if (attempts < 10) setTimeout(tryBind, 200);
    };
    tryBind();
  }

  open() {
    if (this._isOpen) return;
    this._isOpen = true;
    this.removeAttribute('hidden');
    this.classList.add('is-open');
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
    this.dispatchEvent(new CustomEvent('oath:megamenu:open', { bubbles: true }));
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.classList.remove('is-open');
    // Wait for CSS transition (~200ms) before re-hiding via attribute
    // so the close animation can play.
    const HIDE_DELAY = 220;
    setTimeout(() => {
      if (!this._isOpen) this.setAttribute('hidden', '');
    }, HIDE_DELAY);
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'false');
    this.dispatchEvent(new CustomEvent('oath:megamenu:close', { bubbles: true }));
  }

  toggle() { this._isOpen ? this.close() : this.open(); }

  _scheduleOpen() {
    this._clearTimers();
    this._openTimer = setTimeout(() => this.open(), this._openDelay);
  }
  _scheduleClose() {
    this._clearTimers();
    this._closeTimer = setTimeout(() => this.close(), this._closeDelay);
  }
  _clearTimers() {
    if (this._openTimer)  { clearTimeout(this._openTimer);  this._openTimer = null; }
    if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
  }

  _onTriggerClick(e) {
    e.preventDefault();
    this._clearTimers();
    this.toggle();
  }
  _onTriggerEnter() {
    // Hover only matters when a fine pointer is available (desktop).
    if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
    this._scheduleOpen();
  }
  _onTriggerLeave() {
    if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
    this._scheduleClose();
  }
  _onPanelEnter() { this._clearTimers(); }
  _onPanelLeave() {
    if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
    this._scheduleClose();
  }

  _onDocClick(e) {
    if (!this._isOpen) return;
    const t = e.target;
    if (this.contains(t)) return;
    if (this._trigger && this._trigger.contains(t)) return;
    this.close();
  }
  _onDocKey(e) {
    if (e.key === 'Escape' && this._isOpen) {
      this.close();
      if (this._trigger) this._trigger.focus();
    }
  }
}

if (!customElements.get('oath-mega-menu')) {
  customElements.define('oath-mega-menu', OathMegaMenu);
}
